import { useEffect, useRef, useState } from 'react';
import { startCase } from 'lodash';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import StandardModal, { ColorThemeName } from 'components/shared/StandardModal';
import { parseDate } from 'lib/date';
import { WorkflowAction } from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

import { RowButton } from './categoryCard/styles';
import { INBOUND_REFERRALS_ROUTES } from './constants';

// The duplicate-review body (OLIO-2729, restyled per Figma node 10317:22949): the search
// surfaced other active referrals that may be this same patient. Each candidate is confirmed or
// dismissed on its own — there's no cross-candidate selection to make first. Confirming cancels
// THIS referral, linked to the survivor, and carries its requested facilities onto that referral
// (OLIO-3194), through a confirmation modal (a real consequence, so it still gets a second click).
// Dismissing is local-only and not persisted: it just drops that candidate from the list. Once
// every candidate has been dismissed (or there were none to begin with), the step completes itself
// as no_match — the same outcome the old standalone "Not a Duplicate" button used to record by hand.

// Value content is camelCased by the API middleware (see eligibilityData.ts).
export interface DuplicateCandidate {
  workflowId: string;
  status?: string;
  createdAt?: string;
  matchStrength: string;
  patientFirstName?: string;
  patientLastName?: string;
  patientDob?: string;
  patientGender?: string;
}

interface Props {
  action: WorkflowAction;
  onComplete: (actionId: string, outputs: Record<string, string>) => unknown;
  isCompleting: boolean;
}

const formatDate = (value?: string) => parseDate(value ?? null)?.toLocaleDateString() ?? '—';

export default function DuplicatePatientReview({ action, onComplete, isCompleting }: Props) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState<DuplicateCandidate | null>(null);
  const raw = action.outputFor('duplicate_candidates');
  const candidates = Array.isArray(raw) ? (raw as DuplicateCandidate[]) : [];
  const visible = candidates.filter((candidate) => !dismissed.has(candidate.workflowId));

  // Fires once every candidate the search actually found has been dismissed — never for a
  // malformed/empty payload (candidates.length === 0 from the start), which stays a visible,
  // inert empty state rather than silently resolving corrupt data as "no match". The ref stops a
  // second completion from firing if onComplete's own re-render brings this effect back around.
  const autoCompleted = useRef(false);
  useEffect(() => {
    if (candidates.length === 0 || visible.length > 0 || autoCompleted.current) return;
    autoCompleted.current = true;
    onComplete(action.id, { duplicate_patient_result: 'no_match' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible.length]);

  const dismiss = (workflowId: string) => setDismissed((current) => new Set(current).add(workflowId));

  const confirm = () => {
    if (!confirming) return;
    const candidate = confirming;
    setConfirming(null);
    onComplete(action.id, {
      duplicate_patient_result: 'confirmed_match',
      duplicate_of_workflow_id: candidate.workflowId,
    });
  };

  return (
    <Wrapper data-testid='duplicate-patient-review'>
      <IntroText>
        <BodySmallBold>
          {visible.length} possible existing referral{visible.length === 1 ? '' : 's'} found for this patient
        </BodySmallBold>
        <Explanation>
          Review the matches below. Confirm a match to cancel this referral and add its requested facilities to the
          existing one — otherwise dismiss it and keep working this referral.
        </Explanation>
      </IntroText>

      <Cards>
        {/* Every candidate dismissed: the effect above completes this as no_match on its own, but
            its call can fail silently (the page's onComplete wrapper swallows the error) and leave
            the action stuck at action_required with nothing else on screen to act on. This button
            is the manual escape hatch — same completion, user-triggered instead of automatic. */}
        {candidates.length > 0 && visible.length === 0 && (
          <EmptyState data-testid='duplicate-empty-state'>
            <BodySmall>Every possible match has been dismissed.</BodySmall>
            <RowButton
              variant='outline-primary'
              disabled={isCompleting}
              onClick={() => onComplete(action.id, { duplicate_patient_result: 'no_match' })}
              data-testid='duplicate-mark-no-match'>
              Mark as No Match
            </RowButton>
          </EmptyState>
        )}
        {visible.map((candidate) => (
          <CandidateCard key={candidate.workflowId} data-testid='duplicate-candidate'>
            <CardMain>
              <PatientLink
                to={`${INBOUND_REFERRALS_ROUTES.BASE}/${candidate.workflowId}`}
                target='_blank'
                data-testid='duplicate-candidate-link'>
                {candidate.patientFirstName} {candidate.patientLastName}
              </PatientLink>
              <CardMetaRow>
                <CardMeta>DOB: {formatDate(candidate.patientDob)}</CardMeta>
                <MetaDivider>&nbsp;&nbsp;&nbsp;&nbsp;&bull;&nbsp;&nbsp;&nbsp;&nbsp;</MetaDivider>
                <CardMeta>Gender: {candidate.patientGender ? startCase(candidate.patientGender) : '—'}</CardMeta>
                <MetaDivider>&nbsp;&nbsp;&nbsp;&nbsp;&bull;&nbsp;&nbsp;&nbsp;&nbsp;</MetaDivider>
                <CardMeta>Referral Created: {formatDate(candidate.createdAt)}</CardMeta>
              </CardMetaRow>
            </CardMain>
            <CardActions>
              <RowButton
                variant='outline-gray'
                disabled={isCompleting}
                onClick={() => dismiss(candidate.workflowId)}
                data-testid='duplicate-dismiss'>
                Dismiss
              </RowButton>
              <RowButton
                variant='outline-primary'
                disabled={isCompleting}
                onClick={() => setConfirming(candidate)}
                data-testid='duplicate-confirm'>
                Confirm
              </RowButton>
            </CardActions>
          </CandidateCard>
        ))}
      </Cards>

      {confirming && (
        <StandardModal
          title='Confirm Duplicate'
          onCancel={() => setConfirming(null)}
          colorTheme={ColorThemeName.Primary}>
          <ModalBody>
            <BodySmall>
              Are you sure this referral is a duplicate? This referral will be{' '}
              <BodySmallBold as='span'>canceled</BodySmallBold> and linked to the existing referral you selected. Any
              facilities requested here that the existing referral does not already have will be added to it.
            </BodySmall>
          </ModalBody>
          <ModalButtons>
            <Button variant='ghost' onClick={() => setConfirming(null)}>
              Cancel
            </Button>
            <Button variant='primary' disabled={isCompleting} onClick={confirm} data-testid='duplicate-confirm-modal'>
              Confirm Duplicate
            </Button>
          </ModalButtons>
        </StandardModal>
      )}
    </Wrapper>
  );
}

// The settled note on a completed search that confirmed a duplicate: this run was
// canceled; the link points at the surviving referral.
export function DuplicateLinkNote({ action }: { action: WorkflowAction }) {
  const survivorId = action.outputFor('duplicate_of_workflow_id');
  if (typeof survivorId !== 'string' || !survivorId) return null;

  return (
    <BodySmall data-testid='duplicate-link-note'>
      <ViewLink to={`${INBOUND_REFERRALS_ROUTES.BASE}/${survivorId}`} data-testid='duplicate-survivor-link'>
        View the existing referral
      </ViewLink>
    </BodySmall>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const IntroText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Explanation = styled(BodySmall)`
  color: ${colors.black50};
`;

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  background: ${colors.white};
  border: 1px solid ${colors.black10};
  border-radius: 4px;
`;

// A horizontal row rather than a stack: align-items: center is what centers CardActions against
// the full height of CardMain (name + sublabels together), not just against the name line.
const CandidateCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  background: ${colors.white};
  border: 1px solid ${colors.black10};
  border-radius: 4px;
`;

const CardMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

// The candidate's name is the link into its referral (replacing the old separate "View referral"
// link) — Figma's bold, underlined blue treatment.
const PatientLink = styled(Link)`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.blue100};
  text-decoration: underline;
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const CardMetaRow = styled.div`
  display: flex;
  align-items: center;
`;

const CardMeta = styled(BodySmall)`
  font-size: 12px;
  color: ${colors.black50};
`;

const MetaDivider = styled(BodySmall)`
  font-size: 12px;
  color: ${colors.black50};
`;

const ViewLink = styled(Link)`
  color: ${colors.blue100};
  font-size: 12px;
`;

const ModalBody = styled.div`
  margin: 20px 0 24px;
`;

const ModalButtons = styled(ButtonGroup)`
  justify-content: flex-end;
`;
